/** @format */

import { useBookingStore } from '@/@ducks/bookingNew/hooks/useBookingStore';
import { TRoom } from '@/@ducks/room/declarations';
import { selectorAvailabilityRoom } from '@/@ducks/shared/selectors';
import { selectorBookingAvailability } from '@/ducks/bookingNew/selectors';
import { TTranslation, useAppTranslation } from '@/i18n/declarations';
import { useAppSelector } from '@/redux/hooks';
import { utilityGetPrice } from '@/utils/helpers/utilityGetPrice';
import moment from 'moment';
import 'moment/locale/it';
import { useEffect, useMemo } from 'react';

const usePrice = (room: TRoom, isBooking?: boolean) => {
  const { t, intl } = useAppTranslation();
  const commercialConditions = isBooking ? useBookingStore().commercialConditions : undefined;

  const initialRoomAvailability = useAppSelector(store => selectorAvailabilityRoom(store, room.code));
  const bookingAvailability = useAppSelector(selectorBookingAvailability);

  const { getDiscountUntilDate, hasPromo, getDiscountedPricePercentage, getBasePrice, getFormattedPrice } =
    utilityGetPrice({
      commercialConditions: commercialConditions,
      availability: isBooking ? bookingAvailability : initialRoomAvailability,
      room: room as TRoom,
    });

  useEffect(() => {
    moment.locale(intl.locale);
  }, [intl.locale]);

  const roomHasPromo = useMemo(() => hasPromo(), [hasPromo]);
  const basePrice = useMemo(() => `€${getBasePrice()}`, [getBasePrice]);
  const formattedDiscountUntilDate = useMemo(() => {
    const capitalized = (str: string) =>
      str
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    const endDate = capitalized(moment(getDiscountUntilDate()).format('ll'));
    if (roomHasPromo) return t('plp.card.price.promo' as TTranslation, { endPromo: endDate, price: basePrice });

    return false;
  }, [getDiscountUntilDate, roomHasPromo, basePrice, t, commercialConditions]);
  const formattedPrice = useMemo(() => `€${getFormattedPrice()}`, [getFormattedPrice]);
  const monthPriceText = useMemo(() => `/${t('month')}`, [t]);
  const formattedDiscountedPricePercentage = useMemo(() => {
    const discountedPercentage = Math.floor(getDiscountedPricePercentage());
    return `-${discountedPercentage}%`;
  }, [getDiscountedPricePercentage]);

  return {
    roomHasPromo,
    basePrice,
    formattedDiscountUntilDate,
    formattedPrice,
    monthPriceText,
    formattedDiscountedPricePercentage,
  };
};

export default usePrice;
